/**
 * Typy prowadzonych prac podwykonawcy — lustro `ContractorWorkType` z backendu
 * (apps/contractors/models.py). Firma może mieć kilka naraz (checkboxy w edycji
 * kartoteki); od typów zależą kręgi przy zlecaniu (WPW, MDCP, projektant sprawdzający)
 * i grupy na megamapie podwykonawców.
 */
export type WorkType = "wpw" | "surveyor" | "surveyor_wpw" | "coordinator" | "design_reviewer";

/** Kolejność = kolejność słownika na backendzie (tak też wracają etykiety). */
export const WORK_TYPES: { value: WorkType; label: string; hint: string }[] = [
  { value: "wpw", label: "Negocjator WPW", hint: "Pozyskuje zgody właścicieli (WPW) jako osobne zlecenia." },
  { value: "surveyor", label: "Geodeta", hint: "Mapy do celów projektowych (MDCP) i inne prace geodezyjne." },
  {
    value: "surveyor_wpw",
    label: "Geodeta z WPW",
    hint: "Geodeta, który pozyskuje zgody WPW przy okazji MDCP — zawsze obejmuje też „Geodeta”.",
  },
  { value: "coordinator", label: "Koordynator projektu", hint: "Prowadzi całe zlecenie / projekt ZE." },
  {
    value: "design_reviewer",
    label: "Projektant sprawdzający",
    hint: "Podpisuje klastry dokumentacji projektowej; działa w całej Polsce, nie trafia na megamapę.",
  },
];

export const WORK_TYPE_LABELS: Record<WorkType, string> = Object.fromEntries(
  WORK_TYPES.map((t) => [t.value, t.label]),
) as Record<WorkType, string>;

/** Typ → typy, które z niego wynikają (jak `WORK_TYPE_IMPLIES` na backendzie). */
const IMPLIES: Partial<Record<WorkType, WorkType[]>> = { surveyor_wpw: ["surveyor"] };

/** Lista w kolejności słownika, bez powtórzeń, z typami wynikającymi — to samo, co zapisze backend. */
export function normalizeWorkTypes(values: readonly string[]): WorkType[] {
  const wanted = new Set(values);
  for (const [kind, implied] of Object.entries(IMPLIES) as [WorkType, WorkType[]][]) {
    if (wanted.has(kind)) implied.forEach((k) => wanted.add(k));
  }
  return WORK_TYPES.map((t) => t.value).filter((v) => wanted.has(v));
}

/** Czy typ jest wymuszony przez inny zaznaczony (checkbox zablokowany na „zaznaczone"). */
export function isImpliedWorkType(kind: WorkType, selected: readonly string[]): boolean {
  return (Object.entries(IMPLIES) as [WorkType, WorkType[]][]).some(
    ([by, implied]) => by !== kind && selected.includes(by) && implied.includes(kind),
  );
}

/** Kolor plakietki typu — ten sam na liście, karcie i megamapie. */
export const WORK_TYPE_BADGE: Record<WorkType, string> = {
  wpw: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  surveyor: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  surveyor_wpw: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300",
  coordinator: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  design_reviewer: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300",
};
